import { HTMLAttributes, MouseEventHandler } from "react";
import { Icon, Typography } from "@/components/atoms";
import { clsx } from "@/utils";

export interface SetupPanelHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "onClick" | "title"> {
  stepNumber?: number;
  title: string | React.ReactNode;
  description: string;
  stepCount?: number;
  isLocked?: boolean;
  isCompleted?: boolean;
  isOpen?: boolean;
  showProgressBar?: boolean;
  progressPercentage?: number;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

export const SetupPanelHeader = ({
  stepNumber,
  title,
  description,
  stepCount,
  isLocked = false,
  isCompleted = false,
  isOpen = false,
  showProgressBar = false,
  progressPercentage = 0,
  onClick,
}: SetupPanelHeaderProps) => {
  const isCustomTitle = typeof title !== "string";

  const content = (
    <div className="relative flex flex-col gap-2.5 md:flex-row md:items-center md:justify-between md:gap-0">
      {/* Mobile: Absolute positioned lock icon in top right */}
      {isLocked && <Icon name="Lock1" size="md" className="absolute right-0 top-0 text-neutral-dark-gray md:hidden" />}

      <div className="flex w-full flex-col gap-2.5 md:flex-1 md:flex-row md:items-start md:gap-2.5">
        {isCustomTitle
          ? null
          : stepNumber && (
              <div className={clsx("flex h-6 w-6 items-center justify-center rounded-full bg-neutral-mid-gray")}>
                <Typography variant="title" className="text-neutral-black">
                  {stepNumber}
                </Typography>
              </div>
            )}
        <div className="flex w-full flex-col gap-1 md:flex-1">
          <div className="flex items-center gap-2 md:mb-1">
            {isCustomTitle ? (
              <>{title}</>
            ) : (
              <Typography variant="h2" className="text-neutral-black" id={`setup-panel-header-${stepNumber || "custom"}`}>
                {title}
              </Typography>
            )}
            {/* Desktop: Inline lock icon */}
            {isLocked && <Icon name="Lock1" size="md" className="hidden text-neutral-dark-gray md:inline-flex" />}
            {isCompleted && <Icon isCustom name="check" size="md" className="text-success" />}
          </div>
          <Typography variant="paragraph" className="text-neutral-black">
            {description}
          </Typography>
        </div>
      </div>
      {!isLocked && stepCount && (
        <div className="flex w-full items-center justify-end gap-2.5 md:w-fit">
          <Typography variant="small" className="text-neutral-silver-gray">
            {stepCount} step{stepCount > 1 ? "s" : ""}
          </Typography>
          <Icon name={isOpen ? "ArrowUp2" : "ArrowDown2"} size="md" className="text-neutral-dark-gray" />
        </div>
      )}
    </div>
  );

  if (isLocked) {
    return <div>{content}</div>;
  }

  return (
    <div
      className={clsx("relative", {
        "border-b border-neutral-mid-gray": isOpen,
      })}
    >
      <button
        className="w-full px-5 py-4 text-left outline-none focus:outline-none"
        disabled={isLocked}
        aria-expanded={isOpen}
        aria-controls={`setup-panel-content-${stepNumber}`}
        onClick={onClick}
      >
        {content}
      </button>
      {showProgressBar && isOpen && (
        <div
          className="absolute bottom-[-1] left-0 h-1 rounded-r-full bg-success transition-all duration-300"
          style={{ width: `${Math.min(progressPercentage, 100)}%` }}
        />
      )}
    </div>
  );
};
